import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { AccountCompanyContactPerson } from '../../../../models/account-company-contact-person.model';
import { AccountCompany } from '../../../../models/account-company.model';
import { CompanyService } from '../../../../services/company.service';

import { InformationDialogComponent } from 'root-layout';
import { ILookup, FullPageMaskService, ErrorReportingService } from 'common';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'acc-contact-person',
  templateUrl: './contact-person.component.html',
  styleUrls: ['./contact-person.component.scss'],
})
export class ContactPersonComponent implements OnInit, OnDestroy {
  @Input() salutations: ILookup[];
  @Input() company: AccountCompany;
  @Input() canEdit: boolean;
  @Output() isEditing = new EventEmitter<string>();

  group: UntypedFormGroup;
  isError = false;
  errorMessages = [];

  private _destroy$ = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private errorService: ErrorReportingService,
    private maskService: FullPageMaskService
  ) {}

  ngOnInit(): void {
    this.createForm(this.company.contactPerson);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  edit(formGroup: UntypedFormGroup) {
    this.isError = false;
    formGroup.enable();
    this.isEditing.emit('contact');
  }

  cancel(formGroup: UntypedFormGroup) {
    this.isError = false;
    formGroup.reset(this.company.contactPerson);
    formGroup.disable();
    this.isEditing.emit('');
  }

  save(data: AccountCompanyContactPerson) {
    if (!this.group.valid) {
      return;
    }

    const company: AccountCompany = this.company.getDeepCopy();

    company.contactPerson = data;

    this.maskService.showLoading();
    this.isError = false;

    this.companyService
      .updateCompany(company)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.isError = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
            this.company = company;
            this.group.disable();
            this.isEditing.emit('');
            this.maskService.hideLoading();
          }
        },
        error: (errorPayload) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(errorPayload);
          this.isError = true;
          this.maskService.hideLoading();
        },
      });
  }

  showStatus(): void {
    this.errorService.displayInfo('The contact person was updated successfully');
  }

  createForm(data: AccountCompanyContactPerson) {
    this.group = new UntypedFormGroup({
      salutation: new UntypedFormControl(data.salutation, Validators.required),
      firstName: new UntypedFormControl(data.firstName, Validators.required),
      lastName: new UntypedFormControl(data.lastName, Validators.required),
      nonCompany: new UntypedFormControl(data.nonCompany),
    });

    this.group.disable();
  }
}
